/**
 * Per-clinician week in points — the one selector every screen that shows a
 * clinician's week reads: My week's header, the heat-map hover, the utilization
 * strip, the contested-placement check, the coverage drawer's ceiling chip.
 *
 * Firm = points of real, non-canceled visits on that clinician in the week (or
 * the virtual census beyond the horizon) + assigned plan placeholders inside auth
 * that are not yet real. Provisional = placeholders beyond auth assigned to the
 * clinician + SOC holds on the clinician. Hours stay on the row for the KPI
 * hours engine; they are not the planning unit.
 */
import type { DateKey, DemoState, Discipline, Employment, RosterEntry } from "@caredna-marlin/utils/homeHealthDemo/state";
import { selectAuthCoverage } from "./authLedger";
import { addDaysToKey, dateKeyRange, weekStartKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { clinicianDayFacts } from "./clinicianDay";
import { rosterEntries } from "./roster";
import { pointsFor } from "@caredna-marlin/utils/homeHealthDemo/engine/visitTypes";

export interface WeekPoints {
  handle: string;
  weekStart: DateKey;
  firm: number;
  provisional: number;
  target: number;
  ceiling: number;
  /** Firm points per day, Monday first. */
  byDay: number[];
  /** Hold ids counted as provisional this week. */
  holdIds: string[];
}

export function roundPoints(n: number): number {
  return Math.round(n * 4) / 4;
}

export type PlanVisitLayer = "firm" | "provisional";

/**
 * Memoized by state identity. The reducer never mutates, so a given state object
 * always yields the same layers; the heat map asks for them once per cell and the
 * branch read once per cell again, and each week's clinician hover asks per
 * clinician. Without this, one render is hundreds of passes over the whole plan.
 */
const LAYER_CACHE = new WeakMap<DemoState, Record<string, PlanVisitLayer>>();

/**
 * Every unplaced plan visit, labelled firm (inside auth) or provisional (beyond
 * it). One pass over the plan, auth coverage resolved once per episode: the
 * week selector and the branch read both read this, so a visit can never be firm
 * on one screen and provisional on another.
 */
export function planVisitLayers(state: DemoState): Record<string, PlanVisitLayer> {
  const cached = LAYER_CACHE.get(state);
  if (cached) return cached;
  const out: Record<string, PlanVisitLayer> = {};
  const byEpisode = new Map<string, DemoState["planVisits"][string][]>();
  for (const v of Object.values(state.planVisits)) byEpisode.set(v.episodeId, [...(byEpisode.get(v.episodeId) ?? []), v]);
  for (const [episodeId, visits] of byEpisode) {
    const episode = state.episodes[episodeId];
    if (!episode) continue;
    const coverage = selectAuthCoverage(episode, visits, state.patients[episode.patientRef]?.payer === "MA");
    for (const v of visits) {
      if (v.status !== "placeholder") continue;
      out[v.id] = coverage.byVisitId[v.id] === "beyondAuth" ? "provisional" : "firm";
    }
  }
  LAYER_CACHE.set(state, out);
  return out;
}

/** Plan visits still to be placed (no real visit yet), split by auth coverage. */
export function placeholderPointsFor(
  state: DemoState,
  weekStart: DateKey,
  keep: (v: DemoState["planVisits"][string]) => boolean,
  layers: Record<string, PlanVisitLayer> = planVisitLayers(state),
): { firm: number; provisional: number; firmByDate: Record<DateKey, number> } {
  const weekEnd = addDaysToKey(weekStart, 6);
  let firm = 0;
  let provisional = 0;
  const firmByDate: Record<DateKey, number> = {};
  for (const v of Object.values(state.planVisits)) {
    if (layers[v.id] === undefined || v.targetDate < weekStart || v.targetDate > weekEnd || !keep(v)) continue;
    const points = pointsFor(state.config, v.visitTypeKind);
    if (layers[v.id] === "provisional") provisional += points;
    else {
      firm += points;
      firmByDate[v.targetDate] = (firmByDate[v.targetDate] ?? 0) + points;
    }
  }
  return { firm, provisional, firmByDate };
}

export function selectWeekPoints(state: DemoState, handle: string, weekOf: DateKey): WeekPoints {
  const weekStart = weekStartKey(weekOf);
  const entry = rosterEntries(state).find((e) => e.handle === handle) ?? state.roster[handle];
  const days = dateKeyRange(weekStart, addDaysToKey(weekStart, 6));
  const byDay = days.map(() => 0);
  if (!entry) return { handle, weekStart, firm: 0, provisional: 0, target: 0, ceiling: 0, byDay, holdIds: [] };

  let firm = 0;
  days.forEach((day, i) => {
    const f = clinicianDayFacts(state, entry, day);
    byDay[i] += f.points;
    firm += f.points;
  });
  const placeholders = placeholderPointsFor(state, weekStart, (v) => v.providerHandle === handle);
  firm += placeholders.firm;
  days.forEach((day, i) => {
    byDay[i] += placeholders.firmByDate[day] ?? 0;
  });
  let provisional = placeholders.provisional;
  const holdIds: string[] = [];
  for (const hold of Object.values(state.holds)) {
    if (hold.status !== "held" || hold.providerHandle !== handle || hold.date < weekStart || hold.date > days[6]) continue;
    provisional += pointsFor(state.config, "SOC");
    holdIds.push(hold.id);
  }
  return {
    handle,
    weekStart,
    firm: roundPoints(firm),
    provisional: roundPoints(provisional),
    target: entry.targetPoints,
    ceiling: entry.ceilingPoints,
    byDay: byDay.map(roundPoints),
    holdIds,
  };
}

// ---------------------------------------------------------------------------
// The strip. Points only: no hours column, no Points switch, no comparative
// chips, no status chips and no leadership sort, so the row
// carries no grade and nothing here ranks one clinician against another.
// ---------------------------------------------------------------------------

export interface UtilizationRow {
  handle: string;
  displayName: string;
  discipline: Discipline;
  employment: Employment;
  weekStart: DateKey;
  daysWorked: number;
  bookedMinutes: number;
  driveMinutes: number;
  deliveredMinutes: number;
  hours: number;
  /** Firm points (same number as `selectWeekPoints`). */
  points: number;
  provisionalPoints: number;
  targetHours: number;
  ceilingHours: number;
  targetPoints: number;
  ceilingPoints: number;
  /** True when at least one day of the week reads real visits. */
  mixesRealAndPlanned: boolean;
}

export function utilizationRowFor(state: DemoState, entry: RosterEntry, weekStart: DateKey): UtilizationRow {
  const days = dateKeyRange(weekStart, addDaysToKey(weekStart, 6));
  let bookedMinutes = 0;
  let driveMinutes = 0;
  let deliveredMinutes = 0;
  let daysWorked = 0;
  let mixesRealAndPlanned = false;
  for (const day of days) {
    const f = clinicianDayFacts(state, entry, day);
    if (!f.hasShift) continue;
    daysWorked += 1;
    bookedMinutes += f.bookedMinutes;
    driveMinutes += f.driveMinutes;
    deliveredMinutes += f.deliveredMinutes;
    if (f.isReal) mixesRealAndPlanned = true;
  }
  const hours = Math.round(((bookedMinutes + driveMinutes) / 60) * 10) / 10;
  const week = selectWeekPoints(state, entry.handle, weekStart);
  return {
    handle: entry.handle,
    displayName: entry.displayName,
    discipline: entry.discipline,
    employment: entry.employment,
    weekStart,
    daysWorked,
    bookedMinutes,
    driveMinutes,
    deliveredMinutes,
    hours,
    points: week.firm,
    provisionalPoints: week.provisional,
    targetHours: entry.targetHours,
    ceilingHours: entry.ceilingHours,
    targetPoints: entry.targetPoints,
    ceilingPoints: entry.ceilingPoints,
    mixesRealAndPlanned,
  };
}

/** Alphabetical within discipline: a clinician-facing screen never ranks clinicians by points. */
export function sortAlphabeticalWithinDiscipline(rows: UtilizationRow[]): UtilizationRow[] {
  return [...rows].sort((a, b) => a.discipline.localeCompare(b.discipline) || a.displayName.localeCompare(b.displayName));
}

export function selectUtilizationStrip(state: DemoState, weekOf: DateKey): UtilizationRow[] {
  const weekStart = weekStartKey(weekOf);
  return sortAlphabeticalWithinDiscipline(rosterEntries(state).map((entry) => utilizationRowFor(state, entry, weekStart)));
}
